import {
  useCallback, useEffect, useRef, useState,
} from 'react'
import useLocalStorage from './useLocalStorage'

const usePersistedCountdown = ({
  key, onFinish, initialValue = null, resetOnMount = false,
}) => {
  const [storedCountdownValue, setStoredCountdownValue] = useLocalStorage(
    key,
    initialValue,
    !!initialValue,
    resetOnMount,
  )
  const timeout = useRef(null)
  const [initialCountdownValue, setInitialCountdownValue] = useState({
    time: storedCountdownValue,
    onFinishArgs: {},
  })

  useEffect(() => {
    const stopCountdownFromAnotherTab = (event) => {
      if (event.key !== key) return
      const nextValue = event.newValue === null ? null : JSON.parse(event.newValue)
      if (nextValue !== null && nextValue !== 0) return
      clearTimeout(timeout.current)
      setStoredCountdownValue(null)
      setInitialCountdownValue({ time: null, onFinishArgs: {} })
    }
    window.addEventListener('storage', stopCountdownFromAnotherTab)
    return () => window.removeEventListener('storage', stopCountdownFromAnotherTab)
  }, [key, setStoredCountdownValue])

  useEffect(() => {
    clearTimeout(timeout.current);
    (async function count({ time: remainingSeconds, onFinishArgs }) {
      setStoredCountdownValue(remainingSeconds)
      if (remainingSeconds === 0) await onFinish?.(onFinishArgs)
      timeout.current = setTimeout(() => {
        if (remainingSeconds > 0) {
          count({ time: remainingSeconds - 1, onFinishArgs })
        } else setStoredCountdownValue(null)
      }, 1000)
    }(initialCountdownValue))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCountdownValue])

  const setInitialCountdownValueMemoized = useCallback(
    (time, onFinishArgs = {}) => setInitialCountdownValue({ time, onFinishArgs }),
    [],
  )

  return [
    storedCountdownValue,
    setInitialCountdownValueMemoized,
    () => localStorage.removeItem(key),
  ]
}

export default usePersistedCountdown
